import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { GraduationCap, User, Users, Clock, Star, CheckCircle2, Crown, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const FILTERS = ["All", "Individual", "Group"];

export default function Courses() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get("/courses").then((r) => setCourses(r.data)); }, []);

  const shown = courses.filter((c) => filter === "All" || c.format === filter);

  const enrol = async () => {
    setBusy(true);
    try {
      await api.post("/courses/enroll", { course_slug: active.slug });
      setDone(true);
    } catch {
      toast.error("Could not enrol — please try again");
    }
    setBusy(false);
  };

  const openCourse = (c) => { setActive(c); setDone(false); };

  return (
    <div data-testid="courses-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="h-11 w-11 rounded-2xl bg-secondary/15 flex items-center justify-center"><GraduationCap className="h-6 w-6 text-secondary" /></div>
        <h1 className="font-head text-4xl font-extrabold tracking-tight">Courses & Coaching</h1>
      </div>
      <p className="text-muted-foreground mb-6 max-w-2xl">Intensive individual and group courses run by professional dog trainers and researchers — including specialists in anxiety and complex behaviour.</p>

      <div className="flex gap-2 mb-6">
        {FILTERS.map((f) => (
          <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)} className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${filter === f ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
            {f === "Individual" ? "1:1 Individual" : f} {f !== "All" && `(${courses.filter((c) => c.format === f).length})`}
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {shown.map((c, i) => (
          <motion.div key={c.slug} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            data-testid={`course-${c.slug}`} className="rounded-2xl bg-card border border-border/70 overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-1 transition-all flex flex-col">
            <div className="h-40 bg-muted relative">
              <img src={c.image} alt={c.title} className="h-full w-full object-cover" />
              <span className={`absolute top-3 left-3 text-xs px-2.5 py-1 rounded-full font-medium backdrop-blur ${c.format === "Individual" ? "bg-secondary/90 text-white" : "bg-primary/90 text-white"}`}>{c.format === "Individual" ? "1:1 Individual" : "Group"}</span>
              <span className="absolute top-3 right-3 text-xs px-2.5 py-1 rounded-full bg-card/90 backdrop-blur font-medium">{c.focus}</span>
            </div>
            <div className="p-5 flex flex-col flex-1">
              <h3 className="font-head font-semibold text-lg leading-snug">{c.title}</h3>
              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1.5">
                <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{c.duration}</span>
                <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5" />{c.level}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2 flex-1">{c.description}</p>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/60">
                <div className="h-7 w-7 rounded-full bg-primary/15 flex items-center justify-center text-primary text-xs font-bold">{c.instructor.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}</div>
                <div className="min-w-0"><div className="text-xs font-medium truncate">{c.instructor.name}</div><div className="text-[11px] text-muted-foreground truncate">{c.instructor.role}</div></div>
              </div>
              <div className="flex items-center justify-between mt-4">
                <span className="font-head font-bold text-lg">{c.price}</span>
                <Button data-testid={`enroll-${c.slug}`} onClick={() => setActive(c)} className="rounded-full">View & Join</Button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[88vh] overflow-y-auto scroll-thin">
          {active && !done && (
            <div>
              <img src={active.image} alt={active.title} className="h-40 w-full object-cover rounded-xl mb-4" />
              <DialogHeader><DialogTitle className="font-head text-2xl">{active.title}</DialogTitle></DialogHeader>
              <div className="flex flex-wrap gap-2 my-2">
                <span className="text-xs bg-muted px-2 py-1 rounded-full flex items-center gap-1">{active.format === "Individual" ? <User className="h-3 w-3" /> : <Users className="h-3 w-3" />}{active.format}</span>
                <span className="text-xs bg-muted px-2 py-1 rounded-full">{active.duration}</span>
                <span className="text-xs bg-muted px-2 py-1 rounded-full">{active.sessions}</span>
                <span className="text-xs bg-secondary/15 text-secondary px-2 py-1 rounded-full">{active.next_start}</span>
              </div>
              <p className="text-sm text-foreground/90">{active.description}</p>
              <div className="rounded-xl bg-muted/50 p-3 my-3">
                <div className="text-sm font-semibold">{active.instructor.name}</div>
                <div className="text-xs text-muted-foreground mb-1">{active.instructor.role}</div>
                <p className="text-xs text-muted-foreground">{active.instructor.bio}</p>
              </div>
              <ul className="space-y-1 mb-4">
                {active.highlights.map((h, i) => <li key={i} className="flex items-center gap-2 text-sm"><CheckCircle2 className="h-4 w-4 text-primary shrink-0" />{h}</li>)}
              </ul>
              <div className="border-t border-border pt-4 space-y-3">
                <div className="flex items-center justify-between"><span className="font-head font-bold text-xl">{active.price}</span><span className="text-xs text-muted-foreground">{active.spots} spots available</span></div>
                <Button data-testid="submit-enroll" onClick={enrol} disabled={busy} className="w-full rounded-full">{busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Enrolling...</> : "Request to join — free"}</Button>
                <p className="text-[11px] text-muted-foreground text-center">The trainer will contact you with the next steps.</p>
              </div>
            </div>
          )}
          {active && done && (
            <div className="text-center py-8">
              <CheckCircle2 className="h-14 w-14 text-primary mx-auto mb-3" />
              <h3 className="font-head text-2xl font-bold">You're on the list! 🎉</h3>
              <p className="text-muted-foreground mt-2 max-w-sm mx-auto">Thanks! {active.instructor.name} and the team will reach out about <b>{active.title}</b>.</p>
              <Button onClick={() => setActive(null)} className="rounded-full mt-5">Done</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
